import { PlatformLocation } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators, FormArray } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Observable, Subject, forkJoin, of } from 'rxjs';
import { switchMap, takeUntil } from 'rxjs/operators';

import { User } from '@site-mate/dashpivot-shared-library';

import { OrgService } from 'app/org/org.service';
import { AppHierarchy } from 'app/shared/model/app-hierarchy.enum';
import { ProjectWeb } from 'app/shared/model/project.model';
import { validateFolderName } from 'app/shared/model/regexp-matches.model';
import { TeamWeb } from 'app/shared/model/team.model';
import { ErrorHandler } from 'app/shared/service/error-handler.service';
import { ToastrService } from 'app/shared/service/toastr.service';
import { UserService } from 'app/user/user.service';

@Component({
  selector: 'cc-add-project',
  templateUrl: 'add-project.component.html',
  styleUrls: ['add-project.component.scss'],
})
export class AddProjectComponent implements OnInit {
  private readonly destroy$ = new Subject<void>();
  companyId: string;
  loading = false;
  title: string;
  submitted: boolean;
  form: FormGroup;

  constructor(
    private readonly orgService: OrgService,
    public readonly modal: NgbActiveModal,
    private readonly toastrService: ToastrService,
    protected readonly location: PlatformLocation,
    private readonly userService: UserService,
    private readonly errorHandler: ErrorHandler,
    private readonly fb: FormBuilder,
  ) {}

  ngOnInit(): void {
    this.initForm();
  }

  private initForm() {
    this.form = this.fb.group({
      project: ['', [Validators.required, validateFolderName(AppHierarchy.Project)]],
      teams: this.fb.array([
        this.fb.group({
          name: ['', [Validators.required, validateFolderName(AppHierarchy.Team)]],
        }),
      ]),
    });
  }

  onSubmit() {
    this.form.markAllAsTouched();
    if (this.form.invalid) {
      return;
    }
    this.loading = true;
    this.orgService
      .addProject(this.companyId, this.form.get('project').value.trim())
      .pipe(
        switchMap(this.createTeams.bind(this)),
        switchMap(this.updateProjectAndUser.bind(this)),
        takeUntil(this.destroy$),
      )
      .subscribe(([project, teams]) => {
        this.toastrService.successByKey('projectAdded');
        this.modal.close({ project, teams });
      }, this.handleSubmitError.bind(this));
  }

  private createTeams(project: ProjectWeb): Observable<(ProjectWeb | TeamWeb)[]> {
    const teamsCreation = this.teamsFormArray.controls.map((team) =>
      this.orgService.addTeam(project.id, team.value.name.trim()),
    );

    return forkJoin([of(project), ...teamsCreation]);
  }

  addTeam(): void {
    if (this.teamsFormArray.valid) {
      this.teamsFormArray.push(
        this.fb.group({ name: ['', [Validators.required, validateFolderName(AppHierarchy.Team)]] }),
      );
    }
  }

  removeTeamFromPosition(position: number): void {
    this.teamsFormArray.removeAt(position);
  }

  get teamsFormArray(): FormArray {
    return this.form.get('teams') as FormArray;
  }

  private updateProjectAndUser([project, ...teams]: [ProjectWeb, ...TeamWeb[]]): Observable<
    [ProjectWeb, TeamWeb[], User]
  > {
    project.teams = teams;
    project.isNew = true;
    return forkJoin([of(project), of(teams), this.userService.updateCurrentUser()]);
  }

  private handleSubmitError(error: string | Error): void {
    this.errorHandler.handle(error);
    this.loading = false;
  }
}
